/**
 * Fabric-aware glue for per-layer animation: the `craftaraAnim` property,
 * preset context from an object, and applying a resolved frame.
 */

import type { fabric } from 'fabric';
import type { LayerAnimation, LayerFrameState } from '@canvas/shared-types';
import { hasAnimation, type PresetContext } from './animationPresets';

type AnimatedObject = fabric.Object & { craftaraAnim?: LayerAnimation | null };

/** Read a layer's animation, or null when it has none (or an unknown schema). */
export function readLayerAnimation(obj: fabric.Object): LayerAnimation | null {
  const anim = (obj as AnimatedObject).craftaraAnim;
  if (!anim || anim.v !== 1) return null;
  return anim;
}

/**
 * Set or clear a layer's animation. An animation with no active steps is
 * removed entirely so un-animated layers carry zero bytes.
 */
export function writeLayerAnimation(obj: fabric.Object, anim: LayerAnimation | null): void {
  const o = obj as AnimatedObject;
  if (anim && hasAnimation(anim)) o.craftaraAnim = anim;
  else delete o.craftaraAnim;
}

/** Current authored transform of an object. */
export function readFrameState(obj: fabric.Object): LayerFrameState {
  return {
    left: obj.left ?? 0,
    top: obj.top ?? 0,
    scaleX: obj.scaleX ?? 1,
    scaleY: obj.scaleY ?? 1,
    angle: obj.angle ?? 0,
    opacity: obj.opacity ?? 1,
    skewX: obj.skewX ?? 0,
    skewY: obj.skewY ?? 0,
    visible: obj.visible !== false,
  };
}

/** Write a resolved frame onto an object. Does not setCoords (too costly per frame). */
export function applyLayerState(obj: fabric.Object, state: LayerFrameState): void {
  obj.set({
    left: state.left,
    top: state.top,
    scaleX: state.scaleX,
    scaleY: state.scaleY,
    angle: state.angle,
    opacity: state.opacity,
    skewX: state.skewX,
    skewY: state.skewY,
    visible: state.visible,
  });
}

function originFraction(origin: string | number | undefined, near: string, far: string): number {
  if (typeof origin === 'number') return origin;
  if (origin === near) return 0;
  if (origin === far) return 1;
  if (origin === 'center') return 0.5;
  return 0;
}

/** Preset context for an object at its authored (baseline) scale. */
export function presetContextFor(
  obj: fabric.Object,
  base: LayerFrameState,
  artboard: { width: number; height: number }
): PresetContext {
  return {
    artboard,
    size: {
      width: (obj.width ?? 0) * base.scaleX,
      height: (obj.height ?? 0) * base.scaleY,
    },
    origin: {
      x: originFraction(obj.originX as string | number | undefined, 'left', 'right'),
      y: originFraction(obj.originY as string | number | undefined, 'top', 'bottom'),
    },
  };
}
